import React from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import Landingpage from "./Pages/Landingpage";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import "dayjs/locale/de";
import localizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";
import * as Sentry from "@sentry/react";

import Layout from "./Layouts/Application";

import MainApp from "./Layouts/MainApp";
import VerificationForm from "./Authentication/VerificationForm";
import User from "../types/User";
import { FlashProps } from "../types/View";
import Workspace from "../types/Workspace";
import WorkspaceSelection from "./Workspaces/Selection";
import NewWorkspace from "./Workspaces/New";
import Login from "./Authentication/Login";
import Terms from "./Pages/Terms";
import Privacy from "./Pages/Privacy";
import BetaTest from "./Pages/Beta";
import LegalNotice from "./Pages/Legal";

const browserLang = navigator.language?.substring(0, 2);
const supportedLangs = ['en', 'de'];
const lng = supportedLangs.includes(browserLang) ? browserLang : 'en';
import en from "../locales/en.json";
import de from "../locales/de.json";
import UserOnboarding from "./Onboarding/UserOnboarding";
import WorkspaceOnboarding from "./Onboarding/WorkspaceOnboarding";

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    de: { translation: de }
  },
  lng,
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false
  }
});

dayjs.extend(utc);
dayjs.extend(localeData);
dayjs.locale(i18n.language);
dayjs.extend(localizedFormat);

export type AppProps = {
  user?: User;
  workspace?: Workspace;
  flash?: Array<FlashProps>;
};

const App = (props: AppProps & { sentryDsn?: string }) => {

  if (props.sentryDsn) {
    Sentry.init({
      dsn: props.sentryDsn,
      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      sendDefaultPii: true,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration()
      ],
      // Tracing
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ["localhost", /^https:\/\/www\.lessonice\.com\//],
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
  };

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Landingpage />} />
        <Route path="/terms" element={<Layout variant="info" {...props}><Terms /></Layout>} />
        <Route path="/privacy" element={<Layout variant="info" {...props}><Privacy /></Layout>} />
        <Route path="/beta" element={<Layout variant="info" {...props}><BetaTest /></Layout>} />
        <Route path="/legal" element={<Layout variant="info" {...props}><LegalNotice /></Layout>} />

        <Route
          path="/signup"
          element={
            <Layout variant="info" {...props}>
              <Login {...props} layout="onboarding" />
            </Layout>
          }
        />
        <Route
          path="/login"
          element={
            <Layout variant="info" {...props}>
              <Login {...props} layout="default" />
            </Layout>
          }
        />
        <Route
          path="/verifications/:verificationId"
          element={
            <Layout variant="info" {...props}>
              <VerificationForm />
            </Layout>
          }
        />
        {/* <Route path="/login/forgot" exact element={this.renderLayout({ info: true, view: <ForgotPwdForm /> })} /> */}
        {/* <Route path="/eusers/:userId/authentication/:authId/edit" exact element={this.renderLayout({ info: true, view: <ResetPwdForm /> })} /> */}

        { props.user && (
          <>
            <Route path="/workspaces" element={
              <Layout variant="info" {...props}>
                <WorkspaceSelection user={props.user} />
              </Layout>
            } />
            <Route path="/workspaces/new" element={
              <Layout variant="info" {...props}>
                <NewWorkspace user={props.user} />
              </Layout>
            } />
            <Route path="/onboarding/user" element={
              <Layout variant="info" {...props}>
                <UserOnboarding user={props.user} />
              </Layout>
            } />
            <Route path="/onboarding/workspace" element={
              <Layout variant="info" {...props}>
                <WorkspaceOnboarding user={props.user} />
              </Layout>
            } />
            
            { props.workspace &&
              [
                "/dashboard", 
                "/lessons", "/lessons/new", "/lessons/:lessonId", 
                "/solutions", "/solutions/new", "/solutions/:solutionId",
                "/conversations/:conversationId"
              ].map(path => <Route key={path} path={path} element={
                <MainApp {...props} />
              } />)
            }
          </>
        )}
      </Routes>
    </Router>
  );
};

export default App;
